import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button } from 'reactstrap';
import { Checkbox, Input, Select } from '../partials';
import { transformDbConfigToState, mergeDbConfigsWithDefaults } from './addressFieldConfig';
import './AddressConfigurator.css';

/**
 * AddressConfigurator - A comprehensive address field configuration interface
 * Allows customization of field visibility, requirements, order, labels, and more
 * 
 * Now loads/saves from field_config database table instead of JSON
 */
export default function AddressConfigurator({ 
	typeId, 
	fieldConfigs,  // Array of field_config records from database
	onChange, 
	onSave, 
	onCancel 
}) {
	const [fields, setFields] = useState([]);
	
	// Initialize fields from database configs or defaults
	useEffect(() => {
		if (fieldConfigs && fieldConfigs.length > 0) {
			// Merge database configs with defaults to ensure all fields exist
			const mergedFields = mergeDbConfigsWithDefaults(fieldConfigs, typeId);
			
			// Preserve expanded state if fields already exist
			if (fields.length > 0) {
				const expandedStates = new Map(fields.map(f => [f.key, f.expanded]));
				mergedFields.forEach(field => {
					if (expandedStates.has(field.key)) {
						field.expanded = expandedStates.get(field.key);
					}
				});
			}
			
			setFields(mergedFields);
		} else {
			// No configs in database, use defaults
			const defaultFields = getDefaultFieldsForType(typeId);
			
			// Preserve expanded state if fields already exist
			if (fields.length > 0) {
				const expandedStates = new Map(fields.map(f => [f.key, f.expanded]));
				defaultFields.forEach(field => {
					if (expandedStates.has(field.key)) {
						field.expanded = expandedStates.get(field.key);
					}
				});
			}
			
			setFields(defaultFields);
		}
	}, [typeId, fieldConfigs]);

	// Handle drag and drop reordering
	const handleDragEnd = (result) => {
		if (!result.destination) return;

		const reordered = Array.from(fields);
		const [removed] = reordered.splice(result.source.index, 1);
		reordered.splice(result.destination.index, 0, removed);

		// Update order property
		const updatedFields = reordered.map((field, index) => ({
			...field,
			order: index
		}));

		setFields(updatedFields);
		if (onChange) {
			onChange({ fields: updatedFields });
		}
	};

	// Handle field property changes
	const handleFieldChange = (index, property, value) => {
		const updatedFields = [...fields];
		updatedFields[index] = {
			...updatedFields[index],
			[property]: value
		};
		setFields(updatedFields);
		
		// Only call onChange for actual configuration changes, not UI-only properties
		// UI-only properties: expanded
		// Configuration properties: visible, required, label, placeholder, width, etc.
		const uiOnlyProperties = ['expanded'];
		if (onChange && !uiOnlyProperties.includes(property)) {
			onChange({ fields: updatedFields });
		}
	};

	// Toggle field visibility
	const toggleVisible = (index) => {
		handleFieldChange(index, 'visible', !fields[index].visible);
	};

	// Toggle field requirement
	const toggleRequired = (index) => {
		const field = fields[index];
		// Can't make invisible fields required
		if (!field.visible && !field.required) {
			return;
		}
		handleFieldChange(index, 'required', !field.required);
	};

	// Reset to defaults (will clear field_configs from DB on save)
	const handleReset = () => {
		const defaultFields = getDefaultFieldsForType(typeId);
		setFields(defaultFields);
		if (onChange) {
			onChange({ fields: defaultFields });
		}
	};

	const getFieldIcon = (fieldType) => {
		const icons = {
			text: 'fa-font',
			phone: 'fa-phone',
			email: 'fa-envelope',
			dropdown: 'fa-caret-square-down',
			number: 'fa-hashtag'
		};
		return icons[fieldType] || 'fa-font';
	};

	return (
		<div className="address-configurator">
			<div className="d-flex justify-content-between align-items-center mb-3">
				<h5 className="mb-0">Configure Address Fields</h5>
				<Button size="sm" color="link" onClick={handleReset}>
					<i className="fa fa-undo mr-1" />
					Reset to Defaults
				</Button>
			</div>

			<div className="alert alert-info small mb-3">
				<i className="fa fa-info-circle mr-2" />
				Drag fields to reorder. Use toggles to show/hide fields and set requirements.
				{typeId === 2 && <div className="mt-1"><strong>Project Location:</strong> Company/contact fields are hidden by default.</div>}
				{(typeId === 10 || typeId === 11 || typeId === 12) && (
					<div className="mt-1"><strong>Bonding Company:</strong> Bond number field is available.</div>
				)}
			</div>

			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId="address-fields">
					{(provided, snapshot) => (
						<div 
							ref={provided.innerRef} 
							{...provided.droppableProps}
							className={`address-fields-list ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
						>
							{fields.map((field, index) => (
								<Draggable 
									key={field.key} 
									draggableId={field.key} 
									index={index}
								>
									{(provided, snapshot) => (
										<div
											ref={provided.innerRef}
											{...provided.draggableProps}
											className={`address-field-item ${!field.visible ? 'field-hidden' : ''} ${snapshot.isDragging ? 'dragging' : ''}`}
										>
											<div className="field-item-content">
												<div className="drag-handle" {...provided.dragHandleProps}>
													<i className="fa fa-grip-vertical text-muted" />
												</div>

												<div className="field-info flex-grow-1">
													<div className="field-header">
														<i className={`fa ${getFieldIcon(field.type)} mr-2 text-muted`} />
														<span className="field-name">{field.label}</span>
														<span className="field-key text-muted ml-2">({field.key})</span>
														
														{field.systemRequired && (
															<span className="badge badge-warning ml-2" title="Required by system">
																<i className="fa fa-lock fa-xs" /> System Required
															</span>
														)}
													</div>
												</div>

												<div className="field-controls">
													<div className="custom-control custom-switch mr-3">
														<input
															type="checkbox"
															className="custom-control-input"
															id={`visible-${field.key}`}
															checked={field.visible}
															onChange={() => toggleVisible(index)}
															disabled={field.systemRequired}
														/>
														<label className="custom-control-label" htmlFor={`visible-${field.key}`}>
															Visible
														</label>
													</div>

													<div className="custom-control custom-switch">
														<input
															type="checkbox"
															className="custom-control-input"
															id={`required-${field.key}`}
															checked={field.required}
															onChange={() => toggleRequired(index)}
															disabled={!field.visible || field.systemRequired}
														/>
														<label className="custom-control-label" htmlFor={`required-${field.key}`}>
															Required
														</label>
													</div>
												</div>

												{field.visible && (
													<button
														type="button"
														className="btn btn-sm btn-link text-muted"
														onClick={() => handleFieldChange(index, 'expanded', !field.expanded)}
													>
														<i className={`fa fa-chevron-${field.expanded ? 'up' : 'down'}`} />
													</button>
												)}
											</div>

											{field.visible && field.expanded && (
												<div className="field-details">
													<div className="row">
														<div className="col-md-6">
															<Input
																label="Custom Label"
																value={field.label}
																onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
																placeholder={field.defaultLabel}
																className="form-control-sm"
															/>
														</div>
														<div className="col-md-6">
															<Input
																label="Placeholder"
																value={field.placeholder || ''}
																onChange={(e) => handleFieldChange(index, 'placeholder', e.target.value)}
																className="form-control-sm"
															/>
														</div>
													</div>
													<div className="row mt-2">
														<div className="col-md-6">
															<Select
																label="Field Width"
																value={field.width || 'col'}
																onChange={(e) => handleFieldChange(index, 'width', e.target.value)}
																className="form-control-sm"
															>
																<option value="col">Full Width</option>
																<option value="col-md-6">Half Width</option>
																<option value="col-md-4">One Third</option>
																<option value="col-md-3">One Quarter</option>
																<option value="col-md-2">One Sixth</option>
															</Select>
														</div>
														<div className="col-md-6">
															<Input
																label="Help Text"
																value={field.helpText || ''}
																onChange={(e) => handleFieldChange(index, 'helpText', e.target.value)}
																className="form-control-sm"
																placeholder="Additional help text for users"
															/>
														</div>
													</div>
													{field.type === 'text' && (
														<div className="row mt-2">
															<div className="col-md-6">
																<Input
																	label="Min Length"
																	type="number"
																	value={field.minLength || ''}
																	onChange={(e) => handleFieldChange(index, 'minLength', e.target.value)}
																	className="form-control-sm"
																/>
															</div>
															<div className="col-md-6">
																<Input
																	label="Max Length"
																	type="number"
																	value={field.maxLength || ''}
																	onChange={(e) => handleFieldChange(index, 'maxLength', e.target.value)}
																	className="form-control-sm"
																/>
															</div>
														</div>
													)}
													{field.key === 'phone' && (
														<div className="mt-2">
															<Input
																label="Phone Format Pattern"
																value={field.pattern || ''}
																onChange={(e) => handleFieldChange(index, 'pattern', e.target.value)}
																placeholder="e.g., [0-9]{3}-[0-9]{3}-[0-9]{4}"
																className="form-control-sm"
															/>
														</div>
													)}
												</div>
											)}
										</div>
									)}
								</Draggable>
							))}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>

			<div className="mt-4 d-flex justify-content-between">
				<div className="text-muted small">
					<i className="fa fa-eye mr-1" />
					{fields.filter(f => f.visible).length} visible fields • 
					<i className="fa fa-asterisk ml-2 mr-1" style={{ fontSize: '0.6em' }} />
					{fields.filter(f => f.required).length} required fields
				</div>
				<div>
					{onCancel && (
						<Button color="secondary" onClick={onCancel} className="mr-2">
							Cancel
						</Button>
					)}
					{onSave && (
						<Button color="primary" onClick={() => onSave({ fields })}>
							Save Configuration
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}

/**
 * Get default field configuration based on address type_id
 */
function getDefaultFieldsForType(typeId) {
	const baseFields = [
		{
			key: 'company',
			type: 'text',
			label: 'Company Name',
			defaultLabel: 'Company Name',
			placeholder: '',
			visible: true,
			required: true,
			systemRequired: typeId !== 2, // Project location doesn't need company
			order: 0,
			width: 'col',
			expanded: false
		},
		{
			key: 'name',
			type: 'text',
			label: 'Contact Name',
			defaultLabel: 'Contact Name',
			placeholder: '',
			visible: true,
			required: false,
			systemRequired: false,
			order: 1,
			width: 'col-md-6',
			expanded: false
		},
		{
			key: 'phone',
			type: 'phone',
			label: 'Phone',
			defaultLabel: 'Phone',
			placeholder: '',
			visible: true,
			required: false,
			systemRequired: false,
			order: 2,
			width: 'col-md-6',
			minLength: 7,
			maxLength: 50,
			expanded: false,
			pattern: '^[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$'
		},
		{
			key: 'address',
			type: 'text',
			label: 'Street Address',
			defaultLabel: 'Street Address',
			placeholder: '',
			visible: true,
			required: true,
			systemRequired: true,
			order: 3,
			width: 'col',
			expanded: false
		},
		{
			key: 'address_2',
			type: 'text',
			label: 'Street Address 2',
			defaultLabel: 'Street Address 2',
			placeholder: 'Apt, Suite, Building, Floor, etc.',
			visible: true,
			required: false,
			systemRequired: false,
			order: 4,
			width: 'col',
			expanded: false
		},
		{
			key: 'city',
			type: 'text',
			label: 'City',
			defaultLabel: 'City',
			placeholder: '',
			visible: true,
			required: true,
			systemRequired: true,
			order: 5,
			width: 'col-md-4',
			expanded: false
		},
		{
			key: 'state',
			type: 'dropdown',
			label: 'State',
			defaultLabel: 'State',
			placeholder: '',
			visible: true,
			required: typeId === 2, // Required for project location
			systemRequired: typeId === 2,
			order: 6,
			width: 'col-md-2',
			expanded: false
		},
		{
			key: 'zip',
			type: 'text',
			label: 'Zip',
			defaultLabel: 'Zip',
			placeholder: '',
			visible: true,
			required: true,
			systemRequired: true,
			order: 7,
			width: 'col-md-3',
			expanded: false
		},
		{
			key: 'county',
			type: 'text',
			label: 'County',
			defaultLabel: 'County',
			placeholder: '',
			visible: true,
			required: false,
			systemRequired: false,
			order: 8,
			width: 'col-md-3',
			expanded: false
		},
		{
			key: 'email',
			type: 'email',
			label: 'Email Address',
			defaultLabel: 'Email Address',
			placeholder: '',
			visible: true,
			required: false,
			systemRequired: false,
			order: 9,
			width: 'col',
			pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
			expanded: false
		},
		{
			key: 'parcel_no',
			type: 'text',
			label: 'Parcel Number',
			defaultLabel: 'Parcel Number',
			placeholder: '',
			visible: false, // Hidden by default, shown when addr_plus is enabled
			required: false,
			systemRequired: false,
			order: 10,
			width: 'col',
			expanded: false
		},
		{
			key: 'lot_no',
			type: 'text',
			label: 'Lot Number',
			defaultLabel: 'Lot Number',
			placeholder: '',
			visible: false,
			required: false,
			systemRequired: false,
			order: 11,
			width: 'col-md-6',
			expanded: false
		},
		{
			key: 'block_no',
			type: 'text',
			label: 'Block Number',
			defaultLabel: 'Block Number',
			placeholder: '',
			visible: false,
			required: false,
			systemRequired: false,
			order: 12,
			width: 'col-md-6',
			expanded: false
		}
	];

	// Add bond_no for bonding companies
	if (typeId === 10 || typeId === 11 || typeId === 12) {
		baseFields.unshift({
			key: 'bond_no',
			type: 'text',
			label: 'Bond Number',
			defaultLabel: 'Bond Number',
			placeholder: '',
			visible: true,
			required: false,
			systemRequired: false,
			order: -1,
			width: 'col',
			expanded: false
		});
	}

	// Adjust for project location (type_id = 2)
	if (typeId === 2) {
		return baseFields.map(field => {
			if (['company', 'name', 'phone', 'email'].includes(field.key)) {
				return { ...field, visible: false, required: false };
			}
			return field;
		});
	}

	// Adjust for null type_id (customer)
	if (typeId === null || typeId === 0) {
		return baseFields.map(field => {
			if (field.key === 'company') {
				return { ...field, label: 'Your Customer', defaultLabel: 'Your Customer' };
			}
			return field;
		});
	}

	return baseFields;
}

